import { useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Mic, Square, Loader2 } from "lucide-react";

const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

export default function DictateButton({ onText, disabled }) {
  const [state, setState] = useState("idle"); // idle | listening | transcribing
  const recRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const startSR = () => {
    const rec = new SR();
    rec.lang = "en-US";
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (e) => {
      let finalText = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript;
      }
      if (finalText.trim()) onText(finalText.trim());
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") toast.error("Microphone access is blocked. Allow it in your browser settings.");
      else if (e.error !== "aborted" && e.error !== "no-speech") toast.error("Dictation stopped. Try again.");
      setState("idle");
    };
    rec.onend = () => setState("idle");
    recRef.current = rec;
    try { rec.start(); setState("listening"); } catch { setState("idle"); }
  };

  const startMR = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = ["audio/webm", "audio/mp4", "audio/ogg"].find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stopStream();
        const type = mr.mimeType || "audio/webm";
        const ext = type.includes("mp4") ? "mp4" : type.includes("ogg") ? "ogg" : "webm";
        const blob = new Blob(chunksRef.current, { type });
        if (!blob.size) { setState("idle"); return; }
        setState("transcribing");
        try {
          const fd = new FormData();
          fd.append("file", blob, `dictation.${ext}`);
          const r = await api.post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (r.data.text) onText(r.data.text);
          else toast.error("Didn't catch that — try again.");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Couldn't transcribe. Try again.");
        } finally { setState("idle"); }
      };
      mediaRef.current = mr;
      mr.start();
      setState("listening");
    } catch {
      toast.error("Microphone access is blocked. Allow it in your browser settings.");
      setState("idle");
    }
  };

  const toggle = async () => {
    if (state === "transcribing") return;
    if (state === "listening") {
      if (recRef.current) recRef.current.stop();
      else if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
      return;
    }
    if (SR) startSR();
    else await startMR();
  };

  return (
    <button type="button" data-testid="chat-dictate-button" onClick={toggle} disabled={disabled}
      title={state === "listening" ? "Stop dictation" : "Dictate"} aria-label="Dictate"
      className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 ${
        state === "listening" ? "bg-[#FF5A00] text-[#0a0a0f] animate-pulse" : "text-[#9ca3af] hover:text-white hover:bg-[#0a0a0f]"}`}>
      {state === "transcribing" ? <Loader2 size={18} className="animate-spin" /> : state === "listening" ? <Square size={16} /> : <Mic size={18} />}
    </button>
  );
}
